import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

export type UploadType =  "edit" | "upload" | "admin-upload" | "seller-upload" | "mileage-update"

export enum UploadStatus {
  QUEUED = 'queued',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed'
}


export type UploadDialogState = {
  vehicleId: number | null;
  isOpened: boolean;
  type: UploadType | null;
  vin?: string;
}

export type CameraListDialog = UploadDialogState & {
  initialized: boolean;
}

const defaultUploadState: UploadDialogState = {
  vehicleId: null,
  isOpened: false,
  type: null
};

type ListerUploadVehicleState = {
  openedQuestionsDialog: UploadDialogState;
  openedVinDialog: UploadDialogState;
  openedReuploadDialog: UploadDialogState;
  openedCarInfoDialog: UploadDialogState;
  openedInitialPicturesDialog: UploadDialogState;
  openedFinalReviewDialog: number | null;
  openedImageNotesDialog: string | null;
  cameraImageListOpened: CameraListDialog;
  vehicleSections: Record<number, any>;
  videoUploadStatus: Record<number, UploadStatus>;
}

const initialState: ListerUploadVehicleState = {
  openedQuestionsDialog: defaultUploadState,
  openedCarInfoDialog: defaultUploadState,
  openedVinDialog: defaultUploadState,
  openedReuploadDialog: defaultUploadState,
  openedInitialPicturesDialog: defaultUploadState,
  cameraImageListOpened: {...defaultUploadState, initialized: false },
  openedFinalReviewDialog: null,
  openedImageNotesDialog: null,
  vehicleSections: {},
  videoUploadStatus: {}
};

type OpenPayload = PayloadAction<{vehicleId: number; type: UploadType}>
type NullableOpenPayload = PayloadAction<{vehicleId: number | null; type: UploadType; vin?: string}>

const listerUploadVehicleV2Slice = createSlice({
  name: 'listerUploadVehicleV2',
  initialState,
  reducers: {
    openQuestionsDialog: (state, action: OpenPayload) => {
      const { vehicleId, type } = action.payload;
      state.openedQuestionsDialog = {
        vehicleId,
        isOpened: true,
        type
      }
    },
    closeQuestionsDialog: (state) => {
      state.openedQuestionsDialog = defaultUploadState
    },
    openVinDialog: (state, action: NullableOpenPayload) => {
      const { vehicleId, type, vin } = action.payload;
      state.openedVinDialog = {
        vehicleId,
        isOpened: true,
        type,
        vin
      }
    },
    closeVinDialog: (state) => {
      state.openedVinDialog = defaultUploadState
    },
    openReuploadDialog: (state, action: NullableOpenPayload) => {
      const { vehicleId, type, vin } = action.payload;
      state.openedReuploadDialog = {
        vehicleId,
        isOpened: true,
        type,
        vin
      }
    },
    closeReuploadDialog: (state) => {
      state.openedReuploadDialog = defaultUploadState
    },
    openInitialPictureDialog: (state, action: NullableOpenPayload) => {
      const { vehicleId, type } = action.payload;
      state.openedInitialPicturesDialog = {
        vehicleId,
        isOpened: true,
        type,
      }
    },
    closeInitialPictureDialog: (state) => {
      state.openedInitialPicturesDialog = defaultUploadState
    },
    openCarInfoDialog: (state, action: OpenPayload) => {
      const { vehicleId, type } = action.payload;
      state.openedCarInfoDialog = {
        vehicleId,
        isOpened: true,
        type
      }
    },
    closeCarInfoDialog: (state) => {
      state.openedCarInfoDialog = defaultUploadState
    },
    openImageNotesDialog: (state, action: PayloadAction<string>) => {
      state.openedImageNotesDialog = action.payload
    },
    closeImageNotesDialog: (state) => {
      state.openedImageNotesDialog = null
    },
    updateVehicleSections: (state, action: PayloadAction<{ id: number, data: any }>) => {
      state.vehicleSections[action.payload.id] = {
        ...(state.vehicleSections[action.payload.id] || {}),
        ...action.payload.data
      }
    },
    openFinalReviewDialog: (state, action: PayloadAction<number>) => {
      state.openedFinalReviewDialog = action.payload
    },
    closeFinalReviewDialog: (state) => {
      state.openedFinalReviewDialog = null
    },
    setVideoUploadStatus: (state, action: PayloadAction<{ vehicleId: number; status: UploadStatus }>) => {
      const { vehicleId, status } = action.payload
      state.videoUploadStatus[vehicleId] = status
    },
    toggleImageListStatus: (state) => {
      state.cameraImageListOpened.isOpened = !state.cameraImageListOpened.isOpened 
    },
    initImageList: (state, action: PayloadAction<{ vehicleId: number; isOpened: boolean }>) => {
      const { vehicleId, isOpened } = action.payload
      if (state.cameraImageListOpened.initialized && state.cameraImageListOpened.vehicleId === vehicleId) return
      state.cameraImageListOpened.initialized = true
      state.cameraImageListOpened.isOpened = isOpened
      state.cameraImageListOpened.vehicleId = vehicleId
    }
  }
});

export const useOpenedQuestionsDialog = () => {
  const openedQuestionsDialog = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, UploadDialogState>((state) => state.listerUploadVehicleV2.openedQuestionsDialog)
  return openedQuestionsDialog
}

export const useVehicleSections = () => {
  const data = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, any>((state) => state.listerUploadVehicleV2.vehicleSections)
  return data
}

export const useVinDialogOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, UploadDialogState>((state) => state.listerUploadVehicleV2.openedVinDialog)
  return isOpen
}

export const useReuploadDialogOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, UploadDialogState>((state) => state.listerUploadVehicleV2.openedReuploadDialog)
  return isOpen
}

export const useInitialImageDialogOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, UploadDialogState>((state) => state.listerUploadVehicleV2.openedInitialPicturesDialog)
  return isOpen
}

export const useCarInfoDialogOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, UploadDialogState>((state) => state.listerUploadVehicleV2.openedCarInfoDialog)
  return isOpen
}

export const useImageNotesDialogOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, string | null>((state) => state.listerUploadVehicleV2.openedImageNotesDialog)
  return isOpen
}

export const useFinalReviewDialogOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, number | null>((state) => state.listerUploadVehicleV2.openedFinalReviewDialog)
  return isOpen
}

export const useImageListOpened = () => {
  const isOpen = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, boolean>((state) => state.listerUploadVehicleV2.cameraImageListOpened.isOpened)
  return isOpen
}


export const useVideoUploadStatus = (vehicleId: number) => {
  const map = useSelector<{ listerUploadVehicleV2: ListerUploadVehicleState }, Record<number, UploadStatus>>((state) => state.listerUploadVehicleV2.videoUploadStatus)
  return map[vehicleId]
}
  

export const {
  openQuestionsDialog,
  closeQuestionsDialog,
  updateVehicleSections,
  openCarInfoDialog,
  openVinDialog,
  closeCarInfoDialog,
  closeVinDialog,
  openImageNotesDialog,
  closeImageNotesDialog,
  openFinalReviewDialog,
  closeFinalReviewDialog,
  setVideoUploadStatus,
  toggleImageListStatus,
  initImageList,
  openInitialPictureDialog,
  closeInitialPictureDialog,
  openReuploadDialog,
  closeReuploadDialog
} = listerUploadVehicleV2Slice.actions;

export default listerUploadVehicleV2Slice
